import { Button, ButtonMode, Icon, ICONS, Loader, Popup } from '@creditsights/styleguide-web';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CompareText from '../../../components/compare-text/CompareText';
import useFetchLoanCompareDetails from '../../../hooks/loan-compare/useFetchLoanCompareDetails';

import './LoanCompareDetails.scss';
import '../CompareDetails.scss';
import { fetchLoanCompareDetailsExport } from '../../../services/loan-compare.service';

function LoanCompareDetails(): ReactElement {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [ids, setIds] = useState<string[]>([]);
  const { data, isLoading } = useFetchLoanCompareDetails(ids);
  const [expandAll, setExpandAll] = useState(true);
  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);

  useEffect(() => {
    if (search) {
      setIds(JSON.parse(decodeURI(search.substring(1))));
    } else {
      setIds([]);
    }
  }, [search]);

  const renderLabel = (category, key) => {
    return key
      .replace(category, '')
      .split('|')
      .filter((item) => !!item)
      .join(' - ');
  };

  const renderCell = (cls, value, key?) => (
    <td key={key}>
      <div className="cell">
        <div className={cls}>
          <span>{value || '—'}</span>
        </div>
      </div>
    </td>
  );

  const toggleCategory = (category) => {
    setHiddenCategories((items) => {
      const index = items.indexOf(category);

      if (index === -1) {
        items.push(category);
      } else {
        items.splice(index, 1);
      }

      return [...items];
    });
  };

  const toggleCategories = () => {
    setExpandAll((value) => {
      setHiddenCategories(value ? [...data.categories] : []);
      return !value;
    });
  };

  const updateBenchmark = (id) => {
    setIds((values) => {
      const index = values.indexOf(id);
      values.splice(index, 1);
      return [id, ...values];
    });
  };

  const removeLoan = (id) => {
    setIds((values) => {
      const index = values.indexOf(id);
      values.splice(index, 1);
      return [...values];
    });
  };

  const renderCells = (key) => {
    return ids.map((id) => {
      const main = data.loans[ids[0]].data[key]?.value;
      let value = data.loans[id].data[key]?.value;
      const metric = `${value}`.length > 40 ? 'Text' : null;
      let cls = 'value';

      if (main !== value) {
        if (metric === 'Text') {
          cls = 'value text-changed';
          value = <CompareText text1={main} text2={value} expanded />;
        } else {
          cls = 'value value-changed';
        }
      }

      return dataLoansIdInPlace(data, id) && renderCell(cls, value);
    });
  };

  const downloadClickHandle = async () => {
    const { data, fileName } = await fetchLoanCompareDetailsExport(ids);
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dataLoansIdInPlace = useCallback((data: any, id: string) => Object.keys(data.loans).length > 0 && Object.keys(data.loans).includes(id), []);

  return (
    <div className="cs-loan-compare-details-ctx compare-details">
      {isLoading && (
        <div className="loading-ctx">
          <Loader className="cs-loader" position="center" spinnerSize="25px" />
        </div>
      )}
      {!isLoading && data && data.categories && (
        <>
          <div className="header-ctx">
            <div className="back-link">
              <span onClick={() => navigate(-1)}>
                <i className="angle left icon" /> Back to deals
              </span>
            </div>
            <div className="download">
              <Button mode={ButtonMode.TERTIARY} onClick={downloadClickHandle}>
                <Icon name={ICONS.Download} width={18} height={18} />
              </Button>
            </div>
          </div>
          <div className="toggle" onClick={() => toggleCategories()}>
            {expandAll ? (
              <span>
                <i className="minus square outline icon" /> Collapse
              </span>
            ) : (
              <span>
                <i className="plus square outline icon" /> Expand
              </span>
            )}{' '}
            All
          </div>
          <div className="table-ctx">
            <table className="compare-table">
              <tr>
                {renderCell('header category company', 'Company')}
                {ids.map(
                  (id, index) =>
                    dataLoansIdInPlace(data, id) && (
                      <td key={id}>
                        <div className="cell">
                          <div className="value category company">
                            <div className="text">{data.loans[id]?.borrower}</div>
                            <div className="actions">
                              {index !== 0 && (
                                <Popup
                                  trigger={
                                    <div className="benchmark" onClick={() => updateBenchmark(id)}>
                                      Benchmark
                                    </div>
                                  }
                                  content="Make this benchmark"
                                  position="top center"
                                  basic={false}
                                  offset={[0, 8]}
                                />
                              )}
                              <i className="times icon" onClick={() => removeLoan(id)} />
                            </div>
                          </div>
                        </div>
                      </td>
                    )
                )}
              </tr>
              <tr>
                {renderCell('header', 'Transaction phase')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].phase, id))}
              </tr>
              <tr>
                {renderCell('header', 'LFI ID')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].lfi_id, id))}
              </tr>
              <tr>
                {renderCell('header', 'Industry')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].industry))}
              </tr>
              <tr>
                {renderCell('header', 'Launch Date')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].launch_date))}
              </tr>
              <tr>
                {renderCell('header', 'Purpose')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].purpose))}
              </tr>
              <tr>
                {renderCell('header', 'Corporate Rating @ Close')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].corporate_rating?.join('/')))}
              </tr>
              <tr>
                {renderCell('header', 'S&P Recovery Rating @ Close')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].recovery_rating))}
              </tr>
              <tr>
                {renderCell('header', 'Facility Rating')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].facility_rating?.join('/')))}
              </tr>
              <tr>
                {renderCell('header', 'Lead Arrangers')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].arrangers?.map((item) => item.name).join('; ')))}
              </tr>
              <tr>
                {renderCell('header', 'Ownership | Lead Sponsor')}
                {ids.map((id) => dataLoansIdInPlace(data, id) && renderCell('value', data.loans[id].sponsors?.map((item) => item.name).join('; ')))}
              </tr>
              <tr>
                <td colSpan={ids.length + 1}>
                  <div className="permissions-message">
                    Visibility into the loan data below requires the appropriate loan subscription and private transaction permissions. Email private loan documents to{' '}
                    <a href="mailto:research@covenantreview.com" target="_blank" rel="noreferrer">
                      research@covenantreview.com
                    </a>{' '}
                    for access
                  </div>
                </td>
              </tr>
              {data.categories.map((category) => (
                <React.Fragment key={category}>
                  <tr>
                    <td className="category">
                      <div className="cell">
                        <div className="header category" onClick={() => toggleCategory(category)}>
                          <div className="text">{category}</div>
                          <i className={`angle ${hiddenCategories.indexOf(category) === -1 ? 'up' : 'down'} icon`} />
                        </div>
                      </div>
                    </td>
                    {ids.map((id) => (
                      <td key={id} className="category empty" />
                    ))}
                  </tr>
                  {hiddenCategories.indexOf(category) === -1 &&
                    data.keys[category].map((key) => (
                      <tr key={key}>
                        {renderCell('header', renderLabel(category, key))}
                        {renderCells(key)}
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default LoanCompareDetails;
